import { BoxPanel } from "screens/styles";
import { FormHelperText, Stack, Typography, useTheme, TextField } from "@mui/material";
import { ClearButtonWrapper, QuestionFormRoot, StyledAnswerArea, StyledIcon, StyledIconTextWrapper, StyledSecondaryButton, StyledText } from "./style";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { urlConverter } from "utils";
import { interpolate } from "components/core";
import { extractQuestions } from "utils/src/extractQuestions";
import { useOnlineBookingApi } from "shell";
import { handleHttpCode, questionTypeEnum } from "assets/constants";
import { updateQuestionAnswerDict } from "utils/src/updateQuestionAnswerDict";
import { qnTexts } from "shell/OnlineBookingTheme/themes/_palette";
import { Box } from "@mui/system";
import { QuestionFreeTextSkeleton } from "./Skeleton/QuestionFreeTextSkeleton";
import WarningCreature from "assets/icons/creatures/Warning";
import { useDispatch } from "react-redux";
import { resetUserDataNextSteps, resetUserDataNextWidgets, userDataSet } from "store/userData";
import { questionMaper } from "./utils";

const FreeTextQuestion = forwardRef(({ data, questions, setAnswer, userData, isFreeText, isFirstRender, setNotValidAnswer, questionsWithNotValidAnswers, activeStep, propertyId, properties }, ref) => {
    const theme = useTheme();
    const api = useOnlineBookingApi();

    let { questionType,
        questionConfig,
        buttonSubmit,
        questionKey,
        title,
        additionalDescription,
        hintText,
        isMandatory,
        question,
        helperText,
        warningText,
        label,
        buttonClear,
        executeAfterProxyAnswer,
        executeAfterInternalAnswer,
        executeAfterInternalQuestion,
        executeAfterProxyQuestion
    } = data || {};

    const hasAnswerWebhook = executeAfterProxyAnswer || executeAfterInternalAnswer;
    const hasQuestionWebhook = executeAfterProxyQuestion || executeAfterInternalQuestion;

    const dispatch = useDispatch();

    const [freeTextValue, setFreeTextValue] = useState(questions && questions[questionKey] ? questions[questionKey] : ''); // State for Free Text
    const [hasError, setFreeTextError] = useState(false); // State for free text error
    const [hasFirstTimeValidAnsver, setHasFirstTimeValidAnsver] = useState(false); // State for free text error
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);
    const errorRef = useRef(null);

    const { customerGUID, clientStateData, journeyGUID } = useMemo(() => {
        return {
            customerGUID: userData.customerGUID,
            clientStateData: userData.clientStateData,
            journeyGUID: userData.journeyGUID
        };
    }, [userData]);
    const questionJsonData = useMemo(() => JSON.stringify(questionConfig), [questionConfig]);
    const questionAnswerDict = useMemo(() => extractQuestions(userData), [userData]);
    const stableQuestionAnswerDict = useMemo(() => {
        return clientStateData?.questionAnswerDict || questionAnswerDict;
    }, [clientStateData?.questionAnswerDict, questionAnswerDict]);
    const currentAnswerFreeText = useMemo(() => {
        if (!isFreeText) return;
        if (!questions || !questionKey) return [];
        return (
            questions[questionKey]
        );
    }, [questions, questionKey, isFreeText]);

    const isUserDataLoaded = !!((clientStateData && clientStateData?.customerGUID && clientStateData?.journeyGUID) || (userData && userData.journeyGUID));

    const { isLoading: isQuestionsLoading, data: questionsData, error: webHooQuestionkError, refetch } = api.questions.useQuestionQuery(
        clientStateData?.customerGUID || customerGUID,
        clientStateData?.journeyGUID || journeyGUID,
        questionAnswerDict,
        questionType || questionTypeEnum.freeText,
        questionJsonData || null,
        propertyId,
        clientStateData?.serviceId || userData?.service?.id,
        clientStateData?.serviceTypeId || userData?.serviceType?.id,
        clientStateData?.unitId || userData?.location?.id,
        clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
        clientStateData?.calendarIds,
        clientStateData?.selectedSlot,
        clientStateData?.selectedDate,
        isUserDataLoaded,
        userData
    )

    const { mutate: answersMutate, isLoading: isAnsweresLoading, error: answerError, reset: resetAnswer } = api.answers.useAnswerMutation();

    const showQuestion = !!(!isQuestionsLoading && questionsData?.showQuestion);

    const parsedData = !!questionsData?.jsonData && questionMaper(JSON.parse(questionsData?.jsonData))
    if (parsedData) {
        questionType = parsedData?.questionType
        questionConfig = parsedData?.questionConfig
        buttonSubmit = parsedData?.buttonSubmit
        questionKey = parsedData?.questionKey
        title = parsedData?.title
        additionalDescription = parsedData?.additionalDescription
        hintText = parsedData?.hintText
        isMandatory = parsedData?.isMandatory
        question = parsedData?.question
        helperText = parsedData?.helperText
        warningText = parsedData?.warningText
        label = parsedData?.label
        buttonClear = parsedData?.buttonClear
        executeAfterProxyAnswer = parsedData?.executeAfterProxyAnswer
        executeAfterInternalAnswer = parsedData?.executeAfterInternalAnswer
        executeAfterInternalQuestion = parsedData?.executeAfterInternalQuestion
        executeAfterProxyQuestion = parsedData?.executeAfterProxyQuestion
    }


    useEffect(() => {
        if (answerError && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorRef.current.focus();
        }

    }, [answerError]);


    /*** If the question is invalid and loading is done, set answer as "" with current question key, for set appointmen validation on submit. */
    useEffect(() => {
        if (!showQuestion && !isQuestionsLoading && !webHooQuestionkError) {
            setAnswer({ ...questions, [questionKey]: "" });
        }
    }, [showQuestion, isQuestionsLoading]);

    const afterPropertyQuestionsOnActiveStep = userData[`after_property_questions_on_step_${activeStep}`];
    const questionLoadingRef = useRef();
    questionLoadingRef.current = isQuestionsLoading;

    useEffect(() => {
        const refetchThisQuestionAfterCurrent = afterPropertyQuestionsOnActiveStep?.some(item => item?.propertyId === propertyId)
        if (refetchThisQuestionAfterCurrent && !questionLoadingRef.current) {
            setFreeTextValue('')
            refetch()
            resetAnswer()
        }
    }, [refetch, afterPropertyQuestionsOnActiveStep, propertyId]);

    /* If there's an error in answering, remove this question's entry from `questions`.   */
    useEffect(() => {
        if (!isAnsweresLoading && answerError) {
            const { [questionKey]: removedProperty, ...restOfQuestions } = questions;
            setAnswer({ ...restOfQuestions });
        }
    }, [answerError, isQuestionsLoading]);

    useEffect(() => {
        if (isFreeText && isFirstRender) {
            setFreeTextValue(currentAnswerFreeText);
        }
    }, [isFreeText, isFirstRender]);

    useEffect(() => {
        if (!hasError && freeTextValue && !hasFirstTimeValidAnsver) {
            setHasFirstTimeValidAnsver(true)
        }
    }, [hasError, freeTextValue, hasFirstTimeValidAnsver])

    const onChange = (answerKey) => {

        const selectedAnswerConfig = {
            questionKey: questionKey,
            answerValue: answerKey,
        };

        const answersJsonData = JSON.stringify(selectedAnswerConfig);
        const emptySelect = answerKey === '';
        const noQuestionsAndEmptySelect = !questions && emptySelect;

        if (noQuestionsAndEmptySelect) return;
        const { updatedQuestionAnswerDict, updatedClientStateData } = updateQuestionAnswerDict(clientStateData, questionAnswerDict, questionKey, answerKey)
        if (!!questions) {
            const noPreviousSelectAndEmptySelect = !questions[questionKey] && emptySelect
            const previousSelectOrEmptySelect = (questions[questionKey] === answerKey || emptySelect) && ((!isMandatory));

            if (noPreviousSelectAndEmptySelect) return;
            if (previousSelectOrEmptySelect) {
                const { [questionKey]: removedProperty, ...restOfQuestions } = questions;
                setAnswer({ ...restOfQuestions })

                setTimeout(() => {
                    dispatch(userDataSet({
                        clientStateData: { questionAnswerDict: { ...restOfQuestions } }
                    }))
                })

                setTimeout(() => {
                    dispatch(resetUserDataNextWidgets({ properties, propertyId }));
                });

                setTimeout(() => {
                    dispatch(resetUserDataNextSteps());
                }, 30);

                if (!hasAnswerWebhook)
                    return
                answersMutate(
                    {// TODO RC: validate Answere check
                        customerGUID: clientStateData?.customerGUID || customerGUID,
                        journeyGUID: clientStateData?.journeyGUID || journeyGUID,
                        questionAnswerDict: (!!clientStateData?.questionAnswerDict && updatedClientStateData.questionAnswerDict) || updatedQuestionAnswerDict,
                        questionType: questionType || questionTypeEnum.freeText,
                        jsonData: answersJsonData || null,
                        propertyId: propertyId,
                        serviceId: clientStateData?.serviceId || userData?.service?.id,
                        serviceTypeId: clientStateData?.serviceTypeId || userData?.serviceType?.id,
                        unitId: clientStateData?.unitId || userData?.location?.id,
                        appointmentTypeId: clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
                        calendarIds: clientStateData?.calendarIds,
                        selectedSlot: clientStateData?.selectedSlot,
                        selectedDate: clientStateData?.selectedDate,
                    }
                );
            }
        }
        if (hasAnswerWebhook) {
            answersMutate(
                {// TODO RC: validate Answere check
                    customerGUID: clientStateData?.customerGUID || customerGUID,
                    journeyGUID: clientStateData?.journeyGUID || journeyGUID,
                    questionAnswerDict: (!!clientStateData?.questionAnswerDict && updatedClientStateData.questionAnswerDict) || updatedQuestionAnswerDict,
                    questionType: questionType || questionTypeEnum.freeText,
                    jsonData: answersJsonData || null,
                    propertyId: propertyId,
                    serviceId: clientStateData?.serviceId || userData?.service?.id,
                    serviceTypeId: clientStateData?.serviceTypeId || userData?.serviceType?.id,
                    unitId: clientStateData?.unitId || userData?.location?.id,
                    appointmentTypeId: clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
                    calendarIds: clientStateData?.calendarIds,
                    selectedSlot: clientStateData?.selectedSlot,
                    selectedDate: clientStateData?.selectedDate,
                }
            );
        }

        setAnswer({ ...questions, [questionKey]: answerKey })
        setTimeout(() => {
            dispatch(resetUserDataNextWidgets({ properties, propertyId }));
        });

        setTimeout(() => {
            dispatch(resetUserDataNextSteps());
        });
    }

    const handleFreeTextSubmit = () => {
        if (!hasError && freeTextValue) {
            if (questionsWithNotValidAnswers) {
                setTimeout(() => {
                    const { [questionKey]: removedProperty, ...restOfQuestions } = questionsWithNotValidAnswers;
                    setNotValidAnswer({ ...restOfQuestions })
                }, 100)
            }
            setTimeout(() => {
                onChange(freeTextValue);
            })
            setSubmitDisabled(true); // Disable the submit button

        }
    }

    const handleFreeTextChange = (event) => {
        setFreeTextError(false)
        setSubmitDisabled(false);

        const validInput = (data.questionConfig.hasRegex && !!data.questionConfig.regex?.length)
            ? new RegExp(data.questionConfig.regex).test(event.target.value)
            : true;

        setFreeTextValue(event.target.value);

        if (!validInput || (data.questionConfig.isMandatory && !event.target.value)) {
            setFreeTextError(true)
        }

        if (data.questionConfig.isMandatory) {
            setNotValidAnswer({ ...questionsWithNotValidAnswers, [questionKey]: event.target.value })
        }
    }

    if (isQuestionsLoading) { return <QuestionFreeTextSkeleton /> }

    if (webHooQuestionkError) {
        return (
            <>
                <QuestionFormRoot
                    style={
                        {
                            outline: '1px solid',
                            outlineColor: theme.palette.red[700],
                            borderRadius: '10px',
                        }
                    }
                    ref={ref}>
                    <BoxPanel className="BoxShadow">
                        <StyledIconTextWrapper>

                            <StyledIcon>
                                <WarningCreature />
                            </StyledIcon>
                            <StyledText>
                                {
                                    webHooQuestionkError && (
                                        webHooQuestionkError?.errorCode === handleHttpCode.webHook_Error
                                            ? webHooQuestionkError?.webhookResults?.messages
                                                .map((msg, index) => (
                                                    <div key={index}>
                                                        <span style={{ color: theme.palette.red[700] }} > {msg.message}</span>
                                                    </div>
                                                ))
                                            : <span style={{ color: theme.palette.red[700] }} > {webHooQuestionkError?.title || 'General error'} </span>
                                    )
                                }
                            </StyledText>
                        </StyledIconTextWrapper>
                    </BoxPanel>
                </QuestionFormRoot>
            </>
        )
    }

    if (!isQuestionsLoading && !showQuestion) return null

    return (
        <>
            <QuestionFormRoot
                style={answerError
                    ? {
                        outline: '1px solid',
                        outlineColor: theme.palette.red[700],
                        borderRadius: '10px',
                    } : {}
                }
                ref={ref}>
                <BoxPanel className="BoxShadow">
                    <Stack direction={'row'} justifyContent='space-between'>
                        <div>
                            <Typography variant='h2' style={{ color: `${theme.vars.palette.primary.main}` }}>
                                {title}
                                {(isMandatory) && <span style={{ color: 'var(--mui-palette-red-900)' }}> *</span>}
                            </Typography>
                            <FormHelperText>
                                {interpolate(additionalDescription || question?.additionalText || '', urlConverter, true)}
                            </FormHelperText>
                        </div>
                        {(!isMandatory && !data?.questionConfig?.isMandatory) &&
                            <ClearButtonWrapper>
                                <StyledSecondaryButton disabled={!freeTextValue} onClick={() => {
                                    onChange('')
                                    setFreeTextValue('')
                                    resetAnswer()
                                }}>
                                    {buttonClear || data.question.buttonClear}
                                </StyledSecondaryButton>
                            </ClearButtonWrapper>
                        }
                    </Stack>
                    <StyledAnswerArea>
                        <Box>
                            <FormHelperText
                                style={{
                                    color: qnTexts.darkGreyMediumEmphasis,
                                    fontSize: '14px',
                                    marginBottom: '8px',
                                }}
                            >
                            </FormHelperText>
                            <TextField
                                variant="outlined"
                                fullWidth
                                placeholder={hintText}
                                onChange={handleFreeTextChange}
                                value={freeTextValue}
                                label={label}
                                error={hasError}
                                helperText={hasError ? warningText : helperText}
                            />
                                    <Box sx={{ textAlign: 'end' }}>
                                        <StyledSecondaryButton disabled={hasError || freeTextValue?.length === 0 || isSubmitDisabled} onClick={() => handleFreeTextSubmit()}>
                                            { buttonSubmit }
                                        </StyledSecondaryButton>
                            </Box>
                        </Box >
                    </StyledAnswerArea>
                </BoxPanel>
            </QuestionFormRoot >

            {answerError && (
                <div ref={errorRef} tabIndex={-1}>
                    {
                        answerError?.errorCode === handleHttpCode.webHook_Error ?
                answerError?.webhookResults?.messages.map((msg, index) => (
                    <div key={index}>
                        <span style={{ color: theme.palette.red[700] }} > {msg?.message || warningText}</span>
                    </div>
                )) : <span style={{ color: theme.palette.red[700] }} > {answerError?.title || 'General error'} </span>
                    }
                </div>
            )
            }
        </>
    )
});

export default FreeTextQuestion;